import { useEffect, useState } from "react";
import fetchData from "../../axios/fetchData";
import { telemetry } from "../../axios/serverUrl";
import SelectComponent from "../Input/SelectComponent";
import { Tooltip } from "../../utils/Tooltip";
import {
  formattedDate,
} from "../../axios/formattedDate";

const MAX_AGE_MS = 60 * 60 * 1000;

const coolingTelemetryKeys = {
  coilLeavTemp: ['cc_lat_cal', 'cc_lat',  'cc_lat_avg'],
  chilledWaterTemp: ['chw_dt_cal', 'chw_dt'],
  opLoad: ['cc_airbtus'],
  coilEffect: ['cc_eff'],
  filterTemp: ['pfilters_pd']
}

const heatingTelemetryKeys = {
  coilLeavTemp: ['hc_lat_cal', 'hc_lat',  'hc_lat_avg'],
  chilledWaterTemp: ['hw_dt_cal', 'hw_dt'],
  opLoad: ['hc_airbtus'],
  coilEffect: ['hc_eff'],
  filterTemp: ['pfilters_pd']
}

const PerformanceMetrics = ({ selectedDevice, selectedMode, setLoading }) => {
  const [dataAggregation, setDataAggregation] = useState('latest');
  const [coilData, setCoilData] = useState({});

  useEffect(() => {
      const getTelemetryData = (telemetryData, requiredKeysMap, property) => {
        const keys = requiredKeysMap[property];
        if (!keys) return undefined;

        const now = Date.now();
        let latestValid = null;

        for (const key of keys) {
          const dataArray = telemetryData[key];
          if (!dataArray || dataArray.length === 0) continue;

          const { value, ts } = dataArray[0];
          if (value === undefined || value == null || ts === undefined) continue;

          const age = now - ts;
          const numericValue = Number(value);

          // Data is recent enough -> immediately return it (respect priority)
          if (age <= MAX_AGE_MS) {
            return { value: numericValue, ts };
          }

          // Keep track of freshest (in case all are old)
          if (!latestValid || ts > latestValid.ts) {
            latestValid = { value: numericValue, ts };
          }
        }

      // fallback: return the freshest old value (if all were stale)
      return latestValid;
    };

    const getAverageValuesForTelemetries = async (telemetryData) => {
      const keyDataPromises = Object.keys(telemetryData).map(async (key) => {
        const latestEntry = telemetryData[key]?.[0];
        const latestEntryTs = latestEntry?.ts + 10;

        if (!latestEntryTs) return { key, data: [] };
        
        const startTs = latestEntryTs -  30 * 24 * 60 * 60 * 1000;;
        const url = `${telemetry}/${selectedDevice?.id.entityType}/${selectedDevice?.id.id}/values/timeseries?keys=${key}&startTs=${startTs}&endTs=${latestEntryTs}&interval=2592000000&agg=AVG&orderBy=ASC`;

        const data = await fetchData(
          "GET", url, { "X-Authorization": "Bearer " + localStorage.getItem("token") },
        );
        return data;
      });

      const results = await Promise.all(keyDataPromises);

      // Here we update AVG timestamps with latest timestamps
      results.forEach(entry => {
        const key = Object.keys(entry)?.[0];
        if (telemetryData[key]?.[0]?.ts !== undefined) {
          entry[key][0].ts = telemetryData[key][0].ts;
        }
      });

      // Combine array items into single object for further processing
      var finalResults = Object.assign({}, ...results);

      return finalResults;
    }

    const getFetchChartData = async () => {
      setLoading(true);

      if (!selectedDevice) {
        setLoading(false);
        return;
      }

      const requiredKeys = selectedMode === 'cooling' ? coolingTelemetryKeys : heatingTelemetryKeys;

      const telemetryKeys = Object.values(requiredKeys).flatMap(key => key);
      const url = `${telemetry}/${selectedDevice?.id.entityType}/${selectedDevice?.id.id}/values/timeseries?keys=${telemetryKeys}&limit=100&agg=MAX&orderBy=ASC`;
    
      const telemetryData = await fetchData(
        "GET", url, { "X-Authorization": "Bearer " + localStorage.getItem("token") },
        {}
      );

      if (dataAggregation === 'latest') {
        setCoilData({
          coilLeavTemp: getTelemetryData(telemetryData, requiredKeys, 'coilLeavTemp'),
          chilledWaterTemp: getTelemetryData(telemetryData, requiredKeys, 'chilledWaterTemp'),
          opLoad: getTelemetryData(telemetryData, requiredKeys, 'opLoad'),
          coilEffect: getTelemetryData(telemetryData, requiredKeys, 'coilEffect'),
          filterTemp: getTelemetryData(telemetryData, requiredKeys, 'filterTemp'),
        })
        setLoading(false);
        return;
      }

      const avgResult = await getAverageValuesForTelemetries(telemetryData)

      setCoilData({
        coilLeavTemp: getTelemetryData(avgResult, requiredKeys, 'coilLeavTemp'),
        chilledWaterTemp: getTelemetryData(avgResult, requiredKeys, 'chilledWaterTemp'),
        opLoad: getTelemetryData(avgResult, requiredKeys, 'opLoad'),
        coilEffect: getTelemetryData(avgResult, requiredKeys, 'coilEffect'),
        filterTemp: getTelemetryData(avgResult, requiredKeys, 'filterTemp'),
      })
      setLoading(false);
  }

    getFetchChartData()
  }, [dataAggregation, selectedDevice, selectedMode, setLoading]);

  const getOpLoadValue = (value) => {
    if (value === null || value === undefined) {
      return undefined;
    }
    const processedValue = selectedMode === 'cooling' ? value / 12000 : value / 1000;
    return processedValue.toFixed(0)
  }

  const getTooltipContent = (text, timestamp) => {
    return (
      <>
        <p>{text}</p>
        <b>{`Latest: ${formattedDate(timestamp)}`}</b>
      </>
    )
  }

  return (
    <div className="bg-gray-color performance-metrics">
      <h3>Performance Metrics</h3>
        <div className="perf-metrics-select-box">
        <SelectComponent
          data={[{label: 'Latest', value: 'latest'}, {label: '30 Day Avg', value: 'avg'}]}
          value={dataAggregation}
          onChange={(value) => {
            setDataAggregation(value);
          }}
        />
      </div>

      <>
        <div className="fancy-number">
          <div>Coil Air Leaving Temp (°F)</div>
          <Tooltip
            id="my-tooltip-coil-leaving"
            className="mb-1 ml-3"
            content={getTooltipContent(
              "Shows the air temperature after passing through the coil, indicating real-time cooling or heating performance.",
              coilData?.coilLeavTemp?.ts
            )}
          />
          <div>
            {coilData?.coilLeavTemp?.value?.toFixed(0)}
          </div>
        </div>

        <div className="fancy-number">
          <div>{`${selectedMode === 'cooling' ? 'Chilled' : 'Hot'} Water ∆T (°F)`}</div>
          <Tooltip
            id="my-tooltip-chilled-water"
            className="mb-1 ml-3"
            content={getTooltipContent(
              "Difference between chilled or hot water entering and leaving the coil. Higher Delta T indicates better heat transfer and efficient operation.",
              coilData?.chilledWaterTemp?.ts
            )}
          />
          <div>
            {coilData?.chilledWaterTemp?.value?.toFixed(0)}
          </div>
        </div>

        <div className="fancy-number">
          <div>{`Operating Load (${selectedMode === 'cooling' ? 'Tons' : 'kBTUs/Hr'})`}</div>
          <Tooltip
            id="my-tooltip-op-load"
            className="mb-1 ml-3"
            content={getTooltipContent(
              "Displays the current cooling or heating capacity of the coil, based on real-time operating conditions.",
              coilData?.opLoad?.ts
            )}
          />
          <div>
            {getOpLoadValue(coilData?.opLoad?.value)}
          </div>
        </div>

        <div className="fancy-number">
          <div>Coil Effectiveness (%)</div>
          <Tooltip
            id="my-tooltip-coil-eff"
            className="mb-1 ml-3"
            content={getTooltipContent(
              "Measures how efficiently the coil transfers heat compared to its theoretical maximum performance.",
              coilData?.coilEffect?.ts
            )}
          />
          <div>{coilData?.coilEffect?.value?.toFixed()}</div>
        </div>

        <div className="fancy-number">
          <div>Filter ∆P (" of H<span style={{verticalAlign: 'sub', fontSize: '0.8em', lineHeight: 1}}>2</span>O)</div>
          <Tooltip
            id="my-tooltip-filter"
            className="mb-1 ml-3"
            content={getTooltipContent(
              "Indicates airflow resistance across filters — higher readings suggest dirty or clogged filters affecting system efficiency.",
              coilData?.filterTemp?.ts
            )}
          />
          <div>{coilData?.filterTemp?.value?.toFixed(1)}</div>
        </div>
      </>
    </div>
  );
};
export default PerformanceMetrics;
